import { useMemo, useCallback, useEffect, useRef } from "react";
import {
  QueryObserverResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { DIC_APP_URL, DIC_API_KEY } from "../environment";
import {
  InteractionPayload,
  InteractionResponse,
  InteractionType,
} from "../interfaces/Interactions";
import { useInteractionsStore } from "./useInteractionsStore";
import { useRequestData } from "./requests/useRequest";

const fetcher = async (
  payload: InteractionPayload
): Promise<InteractionResponse> => {
  if (!DIC_APP_URL) {
    throw new Error("URL de API no configurada");
  }

  const response = await axios.post<InteractionResponse>(
    `${DIC_APP_URL}interactions`,
    payload,
    {
      timeout: 10 * 1000,
      headers: {
        "X-API-Key": DIC_API_KEY,
      },
    }
  );

  return response.data;
};

export const useInteractionAlerts = (
  drugs: string[],
  userCode: string,
  patientCode: string,
  requestId: string,
  severity: InteractionType = "MAJOR",
  source: string = "drugs.com"
) => {
  const payload: InteractionPayload = useMemo(
    () => ({
      drugs,
      userCode,
      patientCode,
      requestId,
      filters: { severity, source },
    }),
    [drugs, userCode, patientCode, severity, source, requestId]
  );

  const queryKey = useMemo(
    () => ["interactions", JSON.stringify(payload)],
    [payload]
  );

  const { data, error, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: () => fetcher(payload),
    enabled: false,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 7 * 24 * 60 * 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  return {
    interactions: data?.interactions || [],
    isLoading,
    error,
    refetch,
    hasData: !!data,
  };
};

export const usePrescriptionInteractions = (
  activePrinciple: string,
  enabled: boolean = true
) => {
  const queryClient = useQueryClient();
  const { data } = useRequestData();
  const drugs = useMemo(
    () => data?.details?.map((detail) => detail.activePrinciple) || [],
    [data]
  );
  const { setInteractions } = useInteractionsStore(data?.request.id || "");
  const drugsWithNewMedicine = useMemo(
    () => [...drugs, activePrinciple],
    [drugs, activePrinciple]
  );

  const promiseRef = useRef<Promise<
    QueryObserverResult<InteractionResponse, Error>
  > | null>(null);

  const { isLoading, refetch } = useInteractionAlerts(
    drugsWithNewMedicine,
    data?.request.doctorCode || "",
    data?.request.patientUuid || "",
    data?.request.id || "",
    "MAJOR"
  );

  useEffect(() => {
    if (drugsWithNewMedicine.length < 2 || !enabled) return;
    if (promiseRef.current === null) {
      promiseRef.current = refetch();
    }
    return () => {
      queryClient.cancelQueries({ queryKey: ["interactions"] });
      promiseRef.current = null;
    };
  }, [refetch, drugsWithNewMedicine, enabled, queryClient]);

  const handleRefetch = useCallback(async () => {
    if (!promiseRef.current) return;
    try {
      const response = await promiseRef.current;
      if (response?.data?.interactions) {
        setInteractions(response.data.interactions);
      }
    } catch (error) {
      console.error("Error fetching interactions: ", error);
    } finally {
      promiseRef.current = null;
    }
  }, [setInteractions]);

  return {
    isLoading,
    handleRefetch,
  };
};
